import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import client from "../configs/graphql.config";
import auth from "./FirebaseAuth";
import { FirebaseManagerContext, FirebaseStatus } from "./FirebaseManager";
import { GET_USER } from "../graphql/identity";
import { UPDATE_PUSH_TOKEN } from "../graphql/identity";
import Constants from "expo-constants";
import { Maybe, User } from "../__generated__/graphql";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react-native";
import { AppState, Platform } from "react-native";
import {
    addSilentNotificationListener,
    removeSilentNotificationListener,
} from "../configs/notifications-listeners";
import { useRecoilValue } from "recoil";
import { currentLang } from "../atoms/i18n";
import { useTracking } from "../hooks/tracking";
import { FirebaseAuthTypes } from "@react-native-firebase/auth";

export enum AuthStatus {
    Loading,
    Out,
    In,
}

interface AuthContextInterface {
    status: AuthStatus;
    user?: User | null;
    refreshUser?: () => Promise<Maybe<User> | null | undefined>;
    appExpoPushToken?: string;
    setAppExpoPushToken: (value: string) => void;
    addRefreshListener: (listener: Listener) => () => void;
    signInWithPhoneNumber: (phoneNumber: string) => Promise<FirebaseAuthTypes.ConfirmationResult>;
}

export const AuthManagerContext = React.createContext<AuthContextInterface>({
    status: AuthStatus.Loading,
    appExpoPushToken: undefined,
    setAppExpoPushToken: () => {},
    addRefreshListener: () => () => {},
    signInWithPhoneNumber: (() => {}) as unknown as (
        phoneNumber: string,
    ) => Promise<FirebaseAuthTypes.ConfirmationResult>,
});

export type Listener = (user?: User | null) => Promise<void> | void;

const defaultSignInWithPhoneNumber = async (phoneNumber: string) => {
    return await auth().signInWithPhoneNumber(
        phoneNumber,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        window?.verifier, // todo: @jimmy, do we really need this? It is very strange.
    );
};

interface UpdatePushTokenVariable {
    expoPushToken?: string;
    firebasePushToken?: string;
}

type Props = {
    appFirebasePushToken?: string;
};

const AuthManager: React.FC<PropsWithChildren<Props>> = ({ appFirebasePushToken, children }) => {
    const appState = React.useRef(AppState.currentState);
    const { status: firebaseStatus } = React.useContext(FirebaseManagerContext);
    const [appExpoPushToken, setAppExpoPushToken] = React.useState<string | undefined>();
    const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.Loading);
    const [user, setUser] = useState<User | null>();
    const currentLoggedUserId = React.useRef<string | undefined>();
    const [getCurrentUserDetails] = useLazyQuery(GET_USER, {
        fetchPolicy: "network-only",
    });
    const { login, logout, trackAppOpen } = useTracking();

    const refreshListeners = useRef<Record<string, Listener>>({});

    const addRefreshListener = useCallback((listener: Listener) => {
        const id = uuidv4();
        refreshListeners.current = { ...refreshListeners.current, [id]: listener };

        return () => {
            const { [id]: _deleted, ...others } = refreshListeners.current;
            refreshListeners.current = others;
        };
    }, []);

    const refreshUser = useCallback(async () => {
        if (firebaseStatus !== FirebaseStatus.In) {
            // do not refresh user if we have no firebase user.
            // AuthManager already manage the auth status based on firebase status
            return;
        }
        //eslint-disable-next-line no-console
        console.log("[AuthManager] refreshUser");
        const currentUserDetail = await getCurrentUserDetails();
        let needSignout = false;
        if (currentUserDetail.error) {
            console.warn("Error on getCurrentUserDetails", currentUserDetail.error.networkError);
            // In case of non network error of if JWT has no corresponding firebase user,
            // we signout only if preserveJwtOnError is not set
            if (
                !currentUserDetail.error.networkError ||
                currentUserDetail.error.networkError.message.indexOf("auth/user-not-found") !== -1
            ) {
                console.warn("Error on getCurrentUserDetails Sign-out ", currentUserDetail.error);
                needSignout = true;
            }
        }
        // we were logged in but now server tell us we have no more corresponding user
        // => we need to sign out
        if (currentLoggedUserId.current && currentUserDetail.data?.currentUser === null) {
            needSignout = true;
        }
        if (Constants.expoConfig?.extra?.preserveJwtOnError) {
            // in any ase we keep the user, it may be a database error or a jwt error
            needSignout = false;
        }
        if (needSignout && auth().currentUser) {
            // set loading state so that the screens will return to welcome after signout
            setAuthStatus(AuthStatus.Loading);
            void auth().signOut();
            return null;
        } else if (currentUserDetail.data?.currentUser !== undefined) {
            const currentUser = currentUserDetail.data.currentUser;
            setUser(currentUser);

            for (const listenerId in refreshListeners.current) {
                await refreshListeners.current[listenerId](currentUser);
            }
        }
        return currentUserDetail.data?.currentUser;
    }, [getCurrentUserDetails, refreshListeners, firebaseStatus]);

    const [updatePushToken] = useMutation(UPDATE_PUSH_TOKEN);
    const onEmailVerified = React.useCallback(() => {
        void refreshUser();
    }, [refreshUser]);

    const onKycChanged = React.useCallback(() => {
        void refreshUser();
    }, [refreshUser]);

    const onClientVerify = React.useCallback(() => {
        void refreshUser();
    }, [refreshUser]);

    React.useEffect(() => {
        addSilentNotificationListener("emailVerification", onEmailVerified);
        addSilentNotificationListener("kycChanged", onKycChanged);
        addSilentNotificationListener("clientVerify", onClientVerify);
        return () => {
            removeSilentNotificationListener(onEmailVerified);
            removeSilentNotificationListener(onKycChanged);
            removeSilentNotificationListener(onClientVerify);
        };
    }, [onEmailVerified, onKycChanged, onClientVerify]);

    React.useEffect(() => {
        if (user) {
            const variables: UpdatePushTokenVariable = {};
            let needUpdate = false;
            if (appExpoPushToken && appExpoPushToken !== user.expoPushToken) {
                variables.expoPushToken = appExpoPushToken;
                needUpdate = true;
            }
            if (appFirebasePushToken && appFirebasePushToken !== user.firebasePushToken) {
                variables.firebasePushToken = appFirebasePushToken;
                needUpdate = true;
            }
            if (needUpdate) {
                void updatePushToken({
                    variables,
                });
            }
        }
    }, [user, updatePushToken, appExpoPushToken, appFirebasePushToken]);

    React.useEffect(() => {
        const subscription = AppState.addEventListener("change", (nextAppState) => {
            if (appState.current.match(/inactive|background/) && nextAppState === "active") {
                void trackAppOpen();
                // we want to be sure data are up-to-date when we come back from background
                void client.refetchQueries({
                    include: "active",
                });
                if (auth().currentUser) {
                    void refreshUser();
                }
            }
            appState.current = nextAppState;
        });

        return () => {
            subscription.remove();
        };
    }, [refreshUser, trackAppOpen]);

    React.useEffect(() => {
        if (firebaseStatus === FirebaseStatus.Loading) {
            //eslint-disable-next-line no-console
            console.log("[AuthManager] firebaseStatus === FirebaseStatus.Loading");
            setAuthStatus(AuthStatus.Loading);
        } else if (firebaseStatus === FirebaseStatus.Out) {
            //eslint-disable-next-line no-console
            console.log("[AuthManager] firebaseStatus === FirebaseStatus.Out");
            setUser(undefined);
            setAuthStatus(AuthStatus.Out);
        } else {
            //eslint-disable-next-line no-console
            console.log("[AuthManager] firebaseStatus === FirebaseStatus.In");
            // result of getUserDetails will be managed by next useEffect :-)
            void refreshUser();
        }
    }, [firebaseStatus, refreshUser]);

    React.useEffect(() => {
        /**
         * Logout
         */
        if (!user && currentLoggedUserId.current !== undefined) {
            //eslint-disable-next-line no-console
            console.log("[AuthManager] did logout");
            currentLoggedUserId.current = undefined;
            Sentry.setUser(null);
            void logout();

            setAuthStatus(AuthStatus.Out);
        } else {
            // We are logged with firebase but we don't have any corresponding user in our database
            if (user === null) {
                /**
                 * Need Signup
                 */
                //eslint-disable-next-line no-console
                console.log("[AuthManager] need signup");
                setAuthStatus(AuthStatus.Out);
            } else if (user && currentLoggedUserId.current !== user.id) {
                /**
                 * Login
                 */
                //eslint-disable-next-line no-console
                console.log("[AuthManager] did login");
                if (user.email) {
                    Sentry.setUser({ email: user.email || "" });
                }
                currentLoggedUserId.current = user.id;
                void login(user);
                setAuthStatus(AuthStatus.In);
            }
        }
    }, [user, login, logout, setAuthStatus]);

    const lang = useRecoilValue(currentLang);
    useEffect(() => {
        if (Platform.OS === "web") {
            auth().languageCode = lang;
        } else {
            void auth().setLanguageCode(lang || null);
        }
    }, [lang]);
    return (
        <AuthManagerContext.Provider
            value={{
                status: authStatus,
                user,
                refreshUser,
                appExpoPushToken,
                setAppExpoPushToken,
                addRefreshListener,
                signInWithPhoneNumber: defaultSignInWithPhoneNumber,
            }}
        >
            {children}
        </AuthManagerContext.Provider>
    );
};

type MockedAuthManagerProps = {
    user?: User | null;
    authStatus?: AuthStatus;
    refreshUser?: () => Promise<Maybe<User> | null | undefined>;
    setAppExpoPushToken?: (value: string) => void;
    addRefreshListener?: (listener: Listener) => () => void;
    signInWithPhoneNumber?: (phoneNumber: string) => Awaited<FirebaseAuthTypes.ConfirmationResult>;
};

/**
 * User for Storybook and Testing
 */
export const MockedAuthManager: React.FC<PropsWithChildren<MockedAuthManagerProps>> = ({
    children,
    user,
    authStatus,
    refreshUser,
    setAppExpoPushToken = () => {},
    addRefreshListener = () => () => {},
    signInWithPhoneNumber: propsSignInWithPhoneNumber,
}) => {
    authStatus = authStatus || (user ? AuthStatus.In : AuthStatus.Out);

    const signInWithPhoneNumber = useMemo(
        () => propsSignInWithPhoneNumber || defaultSignInWithPhoneNumber,
        [propsSignInWithPhoneNumber],
    ) as (phoneNumber: string) => Promise<FirebaseAuthTypes.ConfirmationResult>;

    return (
        <AuthManagerContext.Provider
            value={{
                status: authStatus,
                user,
                refreshUser,
                setAppExpoPushToken,
                addRefreshListener,
                signInWithPhoneNumber,
            }}
        >
            {children}
        </AuthManagerContext.Provider>
    );
};
export default AuthManager;
